import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Users } from 'lucide-react';
import { type EntitySuggestion, type OrgFunction, type OrgFunctionKind } from '@bliks-io/contracts';
import {
  EntityPickerHighlightedText,
  OrgFunctionCard,
  type EntityPickerConfig,
  type EntityPickerRowMeta,
  type EntityPickerSearchKey,
  type EntityPickerSuggestNew
} from '@bliks-io/ui';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgFunctionPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { usePermissions } from '@/hooks/usePermissions';
import { OrgFunctionCreateDialog } from '../OrgFunctionCreateDialog';
import { OrgFunctionSuggestionDialog } from '../OrgFunctionSuggestionDialog';
import { buildEntityPickerCopy } from '../buildCopy';
import { EntityPickerBucketsList, type BucketSection } from '../shared/EntityPickerBucketsList';
import {
  buildBucketSections,
  buildRowMetaIndex,
  shouldShowBucketView
} from '../shared/buildBucketSections';
import { buildDepartmentFilter, buildDepartmentOptions } from '../shared/buildDepartmentOptions';
import { buildPendingOrgFunctionItems } from '../shared/pendingSuggestionUtils';
import {
  usePickerSlotSuggestions,
  type CreateDialogProps,
  type SuggestionDialogProps,
  type UsePickerSlotSuggestionsResult
} from './usePickerSlotSuggestions';

const SEARCH_KEYS: EntityPickerSearchKey<OrgFunction>[] = [
  { name: 'name', weight: 0.5 },
  { name: 'email', weight: 0.15 },
  { name: 'description', weight: 0.15 },
  { name: 'department.name', weight: 0.2 }
];

export interface OrgFunctionPickerRequestContext {
  sourceStepRecordingId: string;
  sourceStepRecordingPlace:
    | 'successor_org_function'
    | 'assigned_to'
    | 'who_else_does_this'
    | 'helpers_org_function';
  sourceRuleId?: string;
}

export interface UseOrgFunctionPickerConfigArgs {
  open: boolean;
  mode: 'single' | 'multi';
  /**
   * The kind this picker instance lists, suggests, and shows ghost rows for.
   * Presentation-only split (ADR 0004): a pick still lands in the unified
   * `orgFunctions` slot regardless of kind.
   */
  kind: OrgFunctionKind;
  processId?: string;
  recordingId?: string;
  /**
   * Start event node anchor (issue 11): suggestions created here anchor
   * `sourceRecordingId` to this node. Also supplied by node-anchored flows
   * without a recording of their own (resolve-unclear).
   */
  startEventNodeId?: string;
  /**
   * Draft-editor host: create/link post to the draft suggestion routes under
   * this process. The hosts null `recordingId` out when this is set — a draft
   * node is not a recording, so the recommendation query is keyed on the
   * process alone and the draft node reaches the suggestion as
   * `startEventNodeId` / `requestContext.sourceStepRecordingId`.
   */
  draftProcessId?: string;
  requestContext?: OrgFunctionPickerRequestContext;
  selectedIds: string[];
  /** Read-only pending suggestions to show as a ghost section. */
  pendingSuggestions?: EntitySuggestion[];
  /**
   * Host opt-in for direct creation (editor only). With the create
   * permission it wins over a source node: the picker creates instead of
   * suggesting.
   */
  allowCreate?: boolean;
}

export interface UseOrgFunctionPickerConfigResult {
  items: OrgFunction[];
  isLoading: boolean;
  mergedSelectedIds: string[];
  config: EntityPickerConfig<OrgFunction>;
  suggestNew?: EntityPickerSuggestNew;
  /** ReactNode to render adjacent to the picker for the suggest-new flow. */
  renderSuggestDialog: UsePickerSlotSuggestionsResult['renderSuggestDialog'];
}

export function useOrgFunctionPickerConfig({
  open,
  mode,
  kind,
  processId,
  recordingId,
  startEventNodeId,
  draftProcessId,
  requestContext,
  selectedIds,
  pendingSuggestions,
  allowCreate
}: UseOrgFunctionPickerConfigArgs): UseOrgFunctionPickerConfigResult {
  const { t } = useTranslation();
  const { can } = usePermissions();
  // The kind-filtered live OrgFunction list drives the picker; pending
  // suggestions (same kind) are layered on as ghost rows below.
  const { data: orgFunctions = [], isLoading } = useOrgFunctions(kind);
  const { data: departments = [] } = useDepartments();
  const { data: suggestions, isLoading: isLoadingSuggestions } = useOrgFunctionPickerSuggestions({
    processId,
    recordingId,
    enabled: open
  });
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  // The suggest-new dialog inherits this picker's fixed kind — its kind
  // select is gone, so the wrapper is what pins it.
  const SuggestionDialog = useMemo(
    () =>
      function KindFixedOrgFunctionSuggestionDialog(props: SuggestionDialogProps) {
        return <OrgFunctionSuggestionDialog {...props} kind={kind} />;
      },
    [kind]
  );

  const CreateDialog = useMemo(
    () =>
      function KindFixedOrgFunctionCreateDialog(props: CreateDialogProps) {
        return <OrgFunctionCreateDialog {...props} kind={kind} />;
      },
    [kind]
  );

  const slot = usePickerSlotSuggestions({
    mode,
    selectedIds,
    sourceRecordingId: requestContext?.sourceStepRecordingId ?? startEventNodeId,
    // Cache-seed hint only; a no-op unless the process is cached.
    seedProcessId: processId,
    draftProcessId,
    SuggestionDialog,
    allowCreate: !!allowCreate && can('OrgFunction', 'create'),
    CreateDialog
  });

  const departmentOptions = useMemo(() => buildDepartmentOptions(departments), [departments]);

  // Synthesize rows for pending suggestions so they appear in the same search +
  // bucket flow as resolved OrgFunctions (prefixed `_id` round-trips back through
  // `partitionConfirmedIds`). OrgFunction suggestions carry the fields the picker
  // reads, so they slot in as OrgFunctions.
  const pendingItems = useMemo(
    () => buildPendingOrgFunctionItems(pendingSuggestions, kind),
    [pendingSuggestions, kind]
  );

  const orgFunctionList = useMemo<OrgFunction[]>(
    () => [...orgFunctions, ...pendingItems],
    [orgFunctions, pendingItems]
  );

  const rowMetaById = useMemo(() => buildRowMetaIndex(suggestions), [suggestions]);

  const config = useMemo<EntityPickerConfig<OrgFunction>>(() => {
    const baseCopy = buildEntityPickerCopy(t, kind, slot.mode);
    return {
      headerIcon:
        kind === 'team' ? <Users className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />,
      searchKeys: SEARCH_KEYS,
      matchInField: {
        primaryField: 'name',
        fieldLabels: {
          email: t('entityPicker.common.fields.email'),
          description: t('entityPicker.common.fields.description'),
          'department.name': t('entityPicker.common.fields.department')
        }
      },
      departmentFilter: buildDepartmentFilter(
        t,
        departmentOptions,
        departmentFilter,
        setDepartmentFilter
      ),
      getRowMeta: (item): EntityPickerRowMeta => rowMetaById.get(item._id) ?? {},
      getChipName: (item) => item.name ?? '',
      renderRow: (item, ctx) => (
        <OrgFunctionCard
          orgFunction={item}
          variant="with-email"
          itemVariant="default"
          size="xs"
          titleSlot={
            <EntityPickerHighlightedText
              text={item.name ?? ''}
              field="name"
              matches={ctx.matches}
            />
          }
        />
      ),
      bucketing: {
        isLoading: isLoadingSuggestions,
        hasBuckets: (candidates) => shouldShowBucketView(suggestions, candidates, pendingItems),
        renderEmptyQueryView: ({ items, selectedIds: draft, toggleId }) => {
          const filteredById = new Map(items.map((fn) => [fn._id, fn] as const));
          const sections: BucketSection<OrgFunction>[] = buildBucketSections<OrgFunction>({
            byId: filteredById,
            suggestions,
            t,
            config: {
              byDepartmentTitle: (deptName) =>
                deptName
                  ? `${t('entityPicker.orgFunction.buckets.byDepartment')} · ${deptName}`
                  : t('entityPicker.orgFunction.buckets.byDepartment'),
              byDepartmentSubtitle: (peerCount) =>
                t('entityPicker.system.buckets.byDepartmentPeerCount', {
                  count: peerCount
                }),
              byProcessTitle: (processName) =>
                processName
                  ? `${t('entityPicker.orgFunction.buckets.byProcess')} · ${processName}`
                  : t('entityPicker.orgFunction.buckets.byProcess'),
              byProcessSubtitle: (sampleSize) =>
                t('entityPicker.system.buckets.byProcessSampleSize', {
                  count: sampleSize
                })
            }
          });
          // Filter pending items through the department rail too so the
          // pending bucket disappears when the user narrows to a department
          // none of them belong to.
          const visiblePending = pendingItems.filter((fn) => filteredById.has(fn._id));
          if (visiblePending.length > 0) {
            sections.unshift({
              key: 'pendingSuggestions',
              title: t('entityPicker.common.buckets.pendingSuggestions'),
              items: visiblePending
            });
          }
          return (
            <EntityPickerBucketsList<OrgFunction>
              sections={sections}
              selectedIds={draft}
              onToggle={toggleId}
              mode={mode}
              renderItem={(item) => (
                <OrgFunctionCard
                  orgFunction={item}
                  variant="with-email"
                  itemVariant="default"
                  size="xs"
                />
              )}
            />
          );
        }
      },
      copy: baseCopy
    };
  }, [
    t,
    kind,
    suggestions,
    isLoadingSuggestions,
    rowMetaById,
    departmentOptions,
    departmentFilter,
    mode,
    pendingItems,
    slot.mode
  ]);

  return {
    items: orgFunctionList,
    isLoading,
    mergedSelectedIds: selectedIds,
    config,
    suggestNew: slot.suggestNew,
    renderSuggestDialog: slot.renderSuggestDialog
  };
}
